import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { authPortalForUser, useAuth } from '../../contexts/AuthContext';
import { tenantSlugFromHost, tenantSlugFromPathOrHost } from '../../utils/tenantDomains';
import { toast } from 'sonner';
import { Crown } from 'lucide-react';

export default function AuthCallback() {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const exchange = useRef(exchangeSession);
  exchange.current = exchangeSession;
  const attempt = useRef(null);

  useEffect(() => {
    const hash = location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');
    const errorParam = params.get('error');
    let active = true;
    let timer;
    const tenant = tenantSlugFromPathOrHost();
    const prefix = tenantSlugFromHost() ? '' : (tenant ? `/${tenant}` : '');
    const fail = (message) => {
      if (!active) return;
      setError(message);
      toast.error('Please sign in again.');
      timer = setTimeout(() => navigate(`${prefix}/auth`, { replace: true }), 2000);
    };
    if (errorParam || !sessionId) {
      fail(errorParam ? 'Authentication failed. Please sign in again.' : 'No session found. Please sign in again.');
    } else {
      // StrictMode can replay effects. Exchange once, subscribe per mount.
      if (attempt.current?.sessionId !== sessionId) {
        attempt.current = { sessionId, promise: exchange.current(sessionId) };
      }
      attempt.current.promise.then(result => {
        if (!active) return;
        if (!result?.user) { fail('Please sign in again.'); return; }
        toast.success(result.user.tenant_name ? `Welcome to ${result.user.tenant_name}!` : 'Welcome!');
        const nextTenant = result.user.tenant_slug || tenant;
        const nextPrefix = tenantSlugFromHost() ? '' : (nextTenant ? `/${nextTenant}` : '');
        navigate(`${nextPrefix}/${authPortalForUser(result.user) === 'admin' ? 'admin' : 'app'}`, { replace: true });
      }).catch(() => fail('Authentication failed. Please sign in again.'));
    }
    return () => { active = false; clearTimeout(timer); };
  }, [location.hash, navigate]);

  return (
    <div
      className="flex min-h-[100dvh] items-center justify-center px-4 text-center"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
        background: '#0B0E14',
      }}
    >
      <div className="min-w-0 w-full max-w-sm">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/20 flex items-center justify-center animate-pulse">
            <Crown className="w-8 h-8 text-[#D4AF37]" />
          </div>
        </div>
        {error ? (
          <>
            <h2 className="text-xl font-bold text-red-400 mb-2">Authentication Error</h2>
            <p className="break-words text-gray-400 [overflow-wrap:anywhere]">{error}</p>
            <p className="text-sm text-gray-500 mt-2">Redirecting...</p>
          </>
        ) : (
          <>
            <div className="spinner mx-auto mb-4" />
            <h2 className="text-xl font-semibold">Authenticating...</h2>
            <p className="text-gray-400 text-sm mt-2">Please wait while we sign you in</p>
          </>
        )}
      </div>
    </div>
  );
}
